import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Eye, FileText, Loader2, Plus, Trash2 } from "lucide-react";
import { API } from "@/constants";
import Disclaimer from "@/components/shared/Disclaimer";
import Footer from "@/components/shared/Footer";

const ReportsListPage = () => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    axios.get(`${API}/reports`).then(r => setReports(r.data)).finally(() => setLoading(false));
  }, []);
  
  const deleteReport = async (id) => {
    if (!window.confirm("Delete this report?")) return;
    await axios.delete(`${API}/reports/${id}`);
    setReports(reports.filter(r => r.id !== id));
  };
  
  return (
    <main className="min-h-screen bg-slate-50" data-testid="reports-list-page">
      <Disclaimer />
      <div className="container-shell py-8">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold" style={{ fontFamily: "'Fraunces', serif" }}>Reports</h1>
            <p className="text-muted-foreground">Saved matter analytics</p>
          </div>
          <Link to="/tools/analytics/new">
            <Button className="rounded-xl gap-2"><Plus className="h-4 w-4" /> New Analysis</Button>
          </Link>
        </div>
        
        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-muted-foreground" /></div>
        ) : reports.length === 0 ? (
          <Card className="text-center py-12">
            <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground mb-4">No reports yet</p>
            <Link to="/tools/analytics/new"><Button>Create First Report</Button></Link>
          </Card>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {reports.map(report => (
              <Card key={report.id} className="hover:shadow-lg transition-shadow">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                      <FileText className="h-5 w-5 text-primary" />
                    </div>
                    <Badge variant="outline">{report.result?.scores?.data_confidence || 0}%</Badge>
                  </div>
                  <h3 className="font-semibold mb-1 line-clamp-1">{report.title}</h3>
                  <p className="text-sm text-muted-foreground mb-3">{new Date(report.created_at).toLocaleDateString()}</p>
                  <div className="flex gap-2">
                    <Link to={`/reports/${report.id}`} className="flex-1">
                      <Button variant="outline" className="w-full" size="sm"><Eye className="h-4 w-4 mr-1" /> View</Button>
                    </Link>
                    <Button variant="ghost" size="sm" onClick={() => deleteReport(report.id)}>
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
      <Footer />
    </main>
  );
};


export default ReportsListPage;
